import React from 'react';
import {
  Typography,
  TypographyProps,
  Box,
  Link,
  BoxProps,
} from '@mui/material';

type SparkTruncatedTypographyProps = {
  typographyProps?: TypographyProps;
  boxStyles?: BoxProps['sx'];
  children: React.ReactNode;
};

function SparkTruncatedTypography({
  typographyProps,
  children,
  boxStyles,
}: SparkTruncatedTypographyProps) {
  const [isTruncated, setIsTruncated] = React.useState(true);
  const [isOverflow, setIsOverflow] = React.useState(false);
  const textRef = React.useRef<HTMLSpanElement | null>(null);

  const toggleTruncate = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsTruncated(!isTruncated);
  };

  React.useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const { scrollHeight, clientHeight } = textRef.current;
        setIsOverflow(scrollHeight > clientHeight);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, []);

  const { sx, ...restProps } = typographyProps || {};

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        display: isTruncated ? 'flex' : 'block',
        alignItems: 'center',
        ...boxStyles,
      }}
    >
      <Typography
        ref={textRef}
        {...restProps}
        sx={{
          overflow: 'hidden',
          textOverflow: isTruncated ? 'ellipsis' : 'clip',
          display: isTruncated ? '-webkit-box' : 'block',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: isTruncated ? 1 : 'none',
          ...sx,
        }}
      >
        {children}
      </Typography>
      {isOverflow && (
        <Link
          href='#'
          onClick={toggleTruncate}
          sx={{
            display: 'block',
            mt: isTruncated ? 0 : 1,
            position: 'relative',
            background: isTruncated
              ? 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1) 50%)'
              : 'none',
            pl: isTruncated ? 0.5 : 0,
            fontSize: '12px',
          }}
        >
          {isTruncated ? 'more' : 'less'}
        </Link>
      )}
    </Box>
  );
}

export default SparkTruncatedTypography;
